/**
 * SecurityKeyCard — works like OfficerRadioCard.
 *
 * Two indicators:
 *   1. Available — green dot when key is in the rack.
 *                  Click Not Avail → prompt for officer name → goes blue with last name.
 *                  Click blue name row → returns key to rack.
 *   2. Not Avail. — red dot. Click to prompt for name and check out.
 *                   Click again to clear back to available.
 */
import { Card, StatusDot, dotLabelColor } from '../ui'
import { KEY_ICON } from '../../assets'
import { markDirty } from '../../stores/sync'
import type { SecurityKey } from '../../stores/desktop'

interface SecurityKeyCardProps {
  k: SecurityKey
  onUpdate: (patch: Partial<SecurityKey>) => void
}

function lastName(name: string): string {
  const s = name.replace(/\s*\(.*?\)\s*/g, '').trim()
  return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
}

export default function SecurityKeyCard({ k, onUpdate }: SecurityKeyCardProps) {
  const isOos      = !!k.oos
  const isAssigned = !isOos && !!k.assignedTo && k.status !== 'bad'
  const isNotAvail = !isOos && k.status === 'bad' && !k.assignedTo
  const isAvail    = !isOos && !isAssigned && !isNotAvail

  const borderColor = isOos      ? '#fca5a5'
                    : isAssigned ? '#3b82f6'
                    : isNotAvail ? '#f87171'
                                 : '#4ade80'

  const imgActive = isAssigned || isAvail

  function handleAvailable() {
    if (isOos) return
    markDirty()
    onUpdate({ status: 'good', assignedTo: '' })
  }

  function handleNotAvail() {
    if (isOos) return
    markDirty()
    if (isAvail || isAssigned) {
      const name = window.prompt(`Key ${k.number} — Officer's name?`) || ''
      if (name.trim()) {
        onUpdate({ status: 'good', assignedTo: name.trim() })
      } else {
        onUpdate({ status: 'bad', assignedTo: '' })
      }
    } else {
      // Already not available — click again to clear
      onUpdate({ status: 'good', assignedTo: '' })
    }
  }

  return (
    <Card variant="item" borderColor={borderColor} opacity={isOos ? 0.5 : 1}
      style={{ background: 'var(--bg-elevated)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>
        Key {k.number}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flexShrink: 0, width: 60, height: 60 }}>
          <img src={KEY_ICON} alt=""
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              opacity: imgActive ? 1 : 0.4,
              filter:  imgActive ? 'none' : 'grayscale(100%)',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {/* Row 1 — Available / On duty with officer */}
              <div
                onClick={handleAvailable}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  cursor: isAssigned || isNotAvail ? 'pointer' : 'default',
                  opacity: isAvail || isAssigned ? 1 : 0.3,
                }}
              >
                <StatusDot variant={isAssigned ? 'blue' : 'green'} active={isAvail || isAssigned} />
                {isAssigned ? (
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#1d4ed8',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {lastName(k.assignedTo)}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('green', isAvail) }}>
                    Available
                  </span>
                )}
              </div>

              {/* Row 2 — Not Available */}
              <div
                onClick={handleNotAvail}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  cursor: 'pointer', opacity: isNotAvail ? 1 : 0.3,
                }}
              >
                <StatusDot variant="red" active={isNotAvail} />
                <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('red', isNotAvail) }}>
                  Not Avail.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
